import { useRef } from "react";
import { Icon, type IconName } from "../../components/ui";
import { employeeRoles, type EmployeeId } from "../../../shared/employees";
import { useSequence } from "./motion";

/**
 * The hero's living example: one night at an interiors studio. The owner is
 * asleep; an example team (named with the suggested names, owners pick their own) keeps working, and the owner wakes up to a
 * summary. It is an illustration and is labelled "Example" on screen.
 */
interface Beat {
  readonly time: string;
  readonly who: EmployeeId;
  readonly icon: IconName;
  readonly text: string;
  readonly status: string;
  readonly night: boolean;
}

const beats: readonly Beat[] = [
  {
    time: "11:42 pm",
    who: "sales",
    icon: "message",
    text: "Replied to Kavita on WhatsApp in Hinglish — quoted ₹1,50,000 from your price list",
    status: "Replying to Kavita",
    night: true,
  },
  {
    time: "12:20 am",
    who: "reception",
    icon: "clock",
    text: "Booked Kavita a site visit for Saturday 11 am",
    status: "Booking a site visit",
    night: true,
  },
  {
    time: "1:30 am",
    who: "follow_up",
    icon: "refresh",
    text: "Drafted a follow-up for Rohit's quote — waiting for your OK",
    status: "Chasing quiet quotes",
    night: true,
  },
  {
    time: "2:15 am",
    who: "support",
    icon: "shield",
    text: "Urgent: Amit says the sink is leaking — opened a case and replied calmly",
    status: "Handling a complaint",
    night: true,
  },
  {
    time: "6:40 am",
    who: "accounts",
    icon: "rupee",
    text: "₹25,000 advance verified by Razorpay — invoice INV/2026-27/0007 made",
    status: "Making an invoice",
    night: false,
  },
  {
    time: "8:00 am",
    who: "operations",
    icon: "inbox",
    text: "Morning summary sent: 4 enquiries, 1 sale, 2 things need you",
    status: "Report sent",
    night: false,
  },
];

const DURATIONS = [2300, 2200, 2300, 2400, 2400, 3800] as const;
const team = employeeRoles.filter((role) => role.available);

export function TeamBoard() {
  const stage = useRef<HTMLDivElement>(null);
  const step = useSequence(DURATIONS, stage);
  const beat = beats[step];
  const busyRole = employeeRoles.find((role) => role.id === beat.who);
  const feed = beats
    .slice(0, step + 1)
    .reverse()
    .slice(0, 4);

  return (
    <div className="board-stage" ref={stage}>
      <div className="board-glow" aria-hidden="true" />
      <div
        className={`board ${beat.night ? "is-night" : "is-day"}`}
        role="img"
        aria-label="Example night shift: while the owner sleeps, Priya replies to a WhatsApp enquiry, Anjali books a site visit, Arjun drafts a follow-up, Kabir handles an urgent complaint, Raghav makes an invoice after Razorpay confirms a payment, and at 8 am Meera sends the owner a morning summary."
      >
        <div aria-hidden="true">
          <header className="board__head">
            <span className="board__sky">
              <span className="board__moon" />
              <span className="board__sun" />
            </span>
            <span className="board__clock">
              <strong>{beat.time}</strong>
              <small>{beat.night ? "You're asleep" : "Good morning"}</small>
            </span>
            <span className="board__example">Example</span>
          </header>

          <div className="board__crew">
            <ul className="board__avatars">
              {team.map((role) => (
                <li
                  key={role.id}
                  className={beat.who === role.id ? "is-busy" : ""}
                  title={`${role.suggestedName} · ${role.title}`}
                >
                  <span
                    className="avatar"
                    style={{
                      ["--a" as string]: role.hue[0],
                      ["--b" as string]: role.hue[1],
                    }}
                  >
                    {role.suggestedName[0]}
                  </span>
                  <i className="board__dot" />
                </li>
              ))}
            </ul>
            <p className="board__now">
              {busyRole ? (
                <>
                  <strong>{busyRole.suggestedName}</strong> · {busyRole.title} —{" "}
                  {beat.status}
                </>
              ) : (
                <>All {team.length} on duty</>
              )}
            </p>
          </div>

          <ol className="board__feed">
            {feed.map((entry) => {
              const role = employeeRoles.find(
                (candidate) => candidate.id === entry.who,
              );
              return (
                <li key={entry.time} className={entry === beat ? "is-new" : ""}>
                  <span className={`board__icon board__icon--${entry.who}`}>
                    <Icon name={entry.icon} size={14} />
                  </span>
                  <span className="board__text">
                    <small>
                      {entry.time} · {role?.suggestedName}
                    </small>
                    {entry.text}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
      <div className="float-chip float-chip--a board-chip" aria-hidden="true">
        <Icon name="zap" size={14} /> On duty 24×7
      </div>
      <div className="float-chip float-chip--b board-chip" aria-hidden="true">
        <Icon name="shield" size={14} /> Money waits for your OK
      </div>
    </div>
  );
}
